import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';

import { ensureArray } from './utils/mediaQueryUtils';
import { ssrSafeWindow } from './utils/ssrSafeWindow';

export type UseMediaQueryOptions = {
  fallback?: boolean | boolean[];
};

const isBrowserRuntime = typeof window !== 'undefined';

/**
 * Use media query
 *
 * A hook used to detect whether a single or multiple media queries individually
 * match.
 *
 * @example
 *   const [isLargerThan400px] = useMediaQuery('(min-width: 400px)');
 *   const [isSmall, isMedium] = useMediaQuery([
 *     '(min-width: 0px)',
 *     '(min-width: 800px)',
 *   ]);
 *   const [isPrint] = useMediaQuery('(media: print)');
 *
 *   const [isLargerThan400pxInSrr] = useMediaQuery('(min-width: 400px)', {
 *     fallback: true, // returns true when server side rendering when theres no global window object (optional)
 *   });
 */
export function useMediaQuery(
  query: string | string[],
  options: UseMediaQueryOptions = {},
): boolean[] {
  const queriesListeners = useRef<string[]>([]);
  const queries = useRef(ensureArray(query));

  useEffect(() => {
    queries.current = ensureArray(query);
  }, [query]);

  const fallbackQueriesValues = ensureArray(options.fallback).filter(
    (v) => !!v,
  ) as boolean[];

  const [value, setValue] = useState(() => {
    return queries.current.map((query, index) => ({
      media: query,
      matches: fallbackQueriesValues.length
        ? !!fallbackQueriesValues[index]
        : ssrSafeWindow.matchMedia(query).matches,
    }));
  });

  const [debouncedValue, setDebouncedValue] = useState(value);
  useDebounce(() => setDebouncedValue(value), 100, [value]);

  useEffect(() => {
    if (!isBrowserRuntime) {
      return;
    }

    setValue(
      queries.current.map((query) => ({
        media: query,
        matches: ssrSafeWindow.matchMedia(query).matches,
      })),
    );

    const mediaQueryLists = queries.current.map((query) =>
      ssrSafeWindow.matchMedia(query),
    );

    const unattachedMediaQueries = queries.current.filter(
      (currentQuery) => queriesListeners.current.indexOf(currentQuery) === -1,
    );

    // Save query to cache
    queriesListeners.current = [
      ...queriesListeners.current,
      ...unattachedMediaQueries,
    ];

    const unAttachedMediaQueryLists = unattachedMediaQueries.map((query) =>
      ssrSafeWindow.matchMedia(query),
    );

    const mediaQueryListEventHandler = (event: MediaQueryListEvent): void => {
      setValue((previousValue) => {
        return previousValue.map((item) => {
          if (item.media === event.media) {
            return { ...item, matches: event.matches };
          }

          return item;
        });
      });
    };

    unAttachedMediaQueryLists.forEach((mediaQueryList) => {
      mediaQueryList.addEventListener(
        'change',
        mediaQueryListEventHandler as EventListenerOrEventListenerObject,
      );
    });

    return () => {
      mediaQueryLists.forEach((mediaQueryList) => {
        mediaQueryList.removeEventListener(
          'change',
          mediaQueryListEventHandler as EventListenerOrEventListenerObject,
        );
      });
    };
  }, [query]);

  return debouncedValue.map((item) => item.matches);
}
